import { useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, Trash2, Paperclip, X, FileText } from "lucide-react";
import { useChatSession } from "@/hooks/useChatSession";

const ACCEPTED_FILES = "image/jpeg,image/png,image/webp,.pdf,.txt,.csv,.md,.json";

const SUGGESTIONS = [
  "Which carrier is best for an oversize tractor to Houston?",
  "How should I price a 3-stop route around East Texas?",
  "What should I include when shipping a zero-turn mower?",
];

function ChatMessages({ messages, busy, onSuggestion, scrollRef }) {
  return (
    <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3" data-testid="ai-chat-messages">
      {messages.length === 0 && (
        <div className="space-y-3">
          <p className="text-sm text-[#57534E]">Try asking:</p>
          {SUGGESTIONS.map((s, i) => (
            <button
              key={s}
              onClick={() => onSuggestion(s)}
              className="block w-full text-left text-sm p-3 rounded-lg border border-border hover:border-[#F97316] hover:bg-[#FFF7ED] transition-colors"
              data-testid={`ai-suggestion-${i}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      {messages.map((m, i) => (
        <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
          <div
            className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === "user"
                ? "bg-[#1C1917] text-white rounded-br-sm"
                : "bg-stone-100 text-[#1C1917] rounded-bl-sm"
            }`}
            data-testid={`ai-msg-${m.role}-${i}`}
          >
            {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
          </div>
        </div>
      ))}
    </div>
  );
}

function AttachmentChips({ attachments, onRemove }) {
  if (!attachments.length) return null;
  return (
    <div className="px-4 pt-2 flex flex-wrap gap-2" data-testid="ai-chat-attachments">
      {attachments.map((a) => (
        <span
          key={a.id}
          className="inline-flex items-center gap-1.5 text-xs bg-[#FFF7ED] text-[#EA580C] border border-[#F97316]/30 rounded-full px-2.5 py-1"
          data-testid={`ai-attachment-chip-${a.id}`}
        >
          <FileText className="h-3 w-3" />
          <span className="max-w-[160px] truncate">{a.name}</span>
          <button onClick={() => onRemove(a.id)} className="hover:text-[#1C1917]">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

export default function AiChat({ open, onOpenChange, sessionId }) {
  const { messages, input, setInput, busy, attachments, addFiles, removeAttachment, send, clear } =
    useChatSession(sessionId, open);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const onScrollUpdate = () =>
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  // Scroll on each render where messages changed
  if (messages.length) setTimeout(onScrollUpdate, 50);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-4 border-b border-border flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="flex items-center gap-2 text-[#1C1917]">
              <Sparkles className="h-5 w-5 text-[#F97316]" /> Shipping Assistant
            </DialogTitle>
            <DialogDescription>Ask about routing, carriers, and pricing.</DialogDescription>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="icon" onClick={clear} className="text-[#57534E]" data-testid="ai-chat-clear">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </DialogHeader>

        <ChatMessages messages={messages} busy={busy} onSuggestion={send} scrollRef={scrollRef} />
        <AttachmentChips attachments={attachments} onRemove={removeAttachment} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="p-4 border-t border-border flex gap-2"
        >
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED_FILES}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
            data-testid="ai-chat-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="shrink-0 border-[#1C1917]/20 text-[#57534E] hover:text-[#F97316]"
            data-testid="ai-chat-attach-button"
            title="Attach documents or images"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the shipping assistant…"
            disabled={busy}
            className="focus-visible:ring-2 focus-visible:ring-[#F97316]/30"
            data-testid="ai-chat-input"
          />
          <Button
            type="submit"
            disabled={busy || (!input.trim() && attachments.length === 0)}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white shrink-0"
            data-testid="ai-chat-send"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
